import { ensureError } from "esa-core";
import { IApiClient } from "esa-hooks";
import { getConfig } from "config";
import ActivityEvent from "event";
import { IEsaLogger } from "services/IEsaLogger";

type LogSeverity = "ERROR" | "WARN" | "INFO" | "DEBUG";
type ProviderUserLogType = "activity" | "console";

const consoleBySeverity = {
  INFO: console.info,
  WARN: console.warn,
  ERROR: console.error,
  DEBUG: console.debug,
};

// V8 は stack の先頭に `name: message` を埋めるため、message を別に送ると重複する。
// ZodError のように toString を上書きする型があるので、先頭行は Error.prototype.toString で組む。
// Firefox / Safari の stack は先頭行を持たないので、一致したときだけ落とす
const getStackFrames = (error: Error): string | undefined => {
  const { stack } = error;

  if (stack === undefined) {
    return undefined;
  }

  const heading = Error.prototype.toString.call(error);

  return stack.startsWith(heading)
    ? stack.slice(heading.length).trimStart()
    : stack;
};

const serializeError = (_key: string, value: unknown): unknown => {
  if (!(value instanceof Error)) {
    return value;
  }

  const serialized = {
    ...value,
    name: value.name,
    stack: getStackFrames(value),
    cause: value.cause,
  };

  // AppError や ZodError は message を own property から組み立てる getter で持つ。
  // own property のときだけ送れば、payload や issues と二重にならない
  return Object.prototype.hasOwnProperty.call(value, "message")
    ? { ...serialized, message: value.message }
    : serialized;
};

class EsaLogger implements IEsaLogger {
  constructor(
    private readonly providerUserUid: string,
    private readonly apiClient: IApiClient,
  ) {}

  request<T>(type: ProviderUserLogType, data: T): void {
    const metadata = {
      userAgent: navigator.userAgent,
      href: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
    };

    const event = { type, event: { ...data, metadata } };
    // 循環参照や BigInt を含むと throw する。ロガーの失敗で画面を落とさない
    let body: string;
    try {
      body = JSON.stringify(event, serializeError);
    } catch (e) {
      body = JSON.stringify({
        type,
        event: {
          metadata,
          serializationError: ensureError(e).message,
        },
      });
    }

    this.apiClient
      .post({
        path: `/provider_users/${this.providerUserUid}/logs`,
        body,
      })
      .catch((e) => {
        console.error(e);
      });
  }

  console<T>(severity: LogSeverity, message: string, data: T): void {
    // 通常のログは常にコンソールにもログを書き出す
    consoleBySeverity[severity](message, data);

    const { logger } = getConfig();

    if (
      !logger.enabled ||
      // エラー以外はログ送信を行わない
      severity !== "ERROR"
    ) {
      return;
    }

    const event = {
      message,
      data,
    };

    this.request("console", event);
  }

  activity(event: ActivityEvent): void {
    const { logger } = getConfig();

    if (!logger.enabled) {
      // ログが無効の場合のみ、アクティビティイベントはコンソールに書き出す
      console.info(event);
      return;
    }

    this.request("activity", event);
  }

  debug<T>(message: string, data: T): void {
    this.console("DEBUG", message, data);
  }

  info<T>(message: string, data: T): void {
    this.console("INFO", message, data);
  }

  warn<T>(message: string, data: T): void {
    this.console("WARN", message, data);
  }

  error<T>(message: string, data: T): void {
    this.console("ERROR", message, data);
  }
}

export default EsaLogger;
